import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    viewChild,
} from '@angular/core';
import {NgbCarousel, NgbCarouselModule, NgbSlideEvent} from '@ng-bootstrap/ng-bootstrap';
import {DateTimePipe, LoadComponent} from '@brunata-angular/ui';
import {CommonModule} from '@angular/common';
import {environment} from 'src/environments/environment';
import {PictureDocument} from './web-archive-document.model';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {TranslatePipe} from '@brunata-angular/util';
import {map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {combineLatest, Observable, ReplaySubject, switchMap, tap} from 'rxjs';
import {DateTime} from 'luxon';

@Component({
    selector: 'app-gallery',
    templateUrl: './gallery.component.html',
    styleUrls: ['./gallery.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslatePipe,
        NgbCarouselModule,
        DateTimePipe,
        CommonModule,
        FontAwesomeModule,
        LoadComponent,
    ],
})
export class GalleryComponent implements OnInit, OnDestroy {
    private readonly carousel = viewChild(NgbCarousel);
    @Input({required: true}) webArchiveDocuments: PictureDocument[] = [];

    currentIndex = 0;

    private readonly http = inject(HttpClient);
    private readonly slidePrefix = 'slide-';
    private readonly webArchiveDocuments$ = new ReplaySubject<PictureDocument[]>(1);
    private readonly objectUrls: string[] = [];

    readonly images$: Observable<
        {
            initials: string;
            objectUrl: string;
            timestamp: DateTime | null;
        }[]
    > = this.webArchiveDocuments$.pipe(
        switchMap(docs =>
            combineLatest(
                docs
                    .filter(doc => doc.docUrl != null)
                    .map(doc =>
                        this.getImageObjectUrl(environment.baseUrl + doc.docUrl).pipe(
                            tap(objectUrl => this.objectUrls.push(objectUrl)),
                            map(objectURL => ({
                                ...doc,
                                objectUrl: objectURL,
                            })),
                        ),
                    ),
            ),
        ),
    );

    ngOnInit(): void {
        this.webArchiveDocuments$.next(this.webArchiveDocuments);
    }

    ngOnDestroy(): void {
        this.objectUrls.forEach(objectUrl => URL.revokeObjectURL(objectUrl));
    }

    carouselSelect(index: number): void {
        this.carousel()?.select(this.slidePrefix + index.toString());
    }

    onSlideChange(event: NgbSlideEvent): void {
        if (event.current.startsWith(this.slidePrefix)) {
            this.currentIndex = parseInt(event.current.slice(this.slidePrefix.length), 10);
        }
    }

    private getImageObjectUrl(url: string): Observable<string> {
        return this.http
            .get(url, {
                responseType: 'blob',
                withCredentials: true,
            })
            .pipe(map(item => URL.createObjectURL(item)));
    }
}
